import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Dialog } from 'primereact/dialog';
import { Dropdown } from 'primereact/dropdown';
import { Button } from '../../../components/ui/Button';
import { useGetVideoWalls } from '../../../queries/videoWall';
import { useGetWallGroups } from '../../../queries/wallGroups';
import { useGetAssignments, useCreateAssignment, useDeleteAssignment } from '../../../queries/wallAssignments';

const DAY_KEYS = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun']; // bit 0..6

/**
 * Content Library quick-assign — assign a library playlist to a wall or group as
 * the ALWAYS-ON default (no time windows). Phase 6 unification: time-of-day
 * scheduling (morning/evening menus) lives ONLY on the visual Schedule page, so
 * this dialog no longer creates time windows — it creates a single always-on
 * assignment (start/end/days null). Existing time-windowed assignments still show
 * in the list (same playlist_assignments table) but are read-only here, with a
 * link to manage them on the Schedule page.
 */
export default function AssignDialog({ playlist, visible, onHide }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { data: walls = [] } = useGetVideoWalls();
  const { data: groups = [] } = useGetWallGroups();
  const { data: existing = [] } = useGetAssignments(playlist ? { playlist_id: playlist.id } : {});
  const create = useCreateAssignment();
  const del = useDeleteAssignment();

  const [targetType, setTargetType] = useState('wall'); // 'wall' | 'group'
  const [groupId, setGroupId] = useState(null);
  const [wallId, setWallId] = useState(null);

  const wallName = useMemo(() => {
    const m = {}; walls.forEach((w) => { m[w.id] = w; }); return m;
  }, [walls]);
  const groupName = useMemo(() => {
    const m = {}; groups.forEach((g) => { m[g.id] = g; }); return m;
  }, [groups]);

  const targetId = targetType === 'group' ? groupId : wallId;

  const submit = async () => {
    if (!targetId) return;
    // Always-on default — no time window. (Schedule page owns time windows.)
    await create.mutateAsync({
      playlist_id: playlist.id,
      ...(targetType === 'group' ? { group_id: groupId } : { wall_id: wallId }),
      start_time: null,
      end_time: null,
      days_of_week: null,
      priority: 0,
    });
    setWallId(null); setGroupId(null);
  };

  const targetLabel = (a) => a.assignable_type === 'wall_group'
    ? `🗂️ ${groupName[a.assignable_id]?.name || a.assignable_id}`
    : (wallName[a.assignable_id]?.name || a.assignable_id);

  // True when an assignment carries a time window (made on the Schedule page).
  const isScheduled = (a) => !!(a.start_time && a.end_time) || !!a.days_of_week;

  const scheduleSummary = (a) => {
    const days = a.days_of_week
      ? DAY_KEYS.filter((_, i) => a.days_of_week & (1 << i)).map((d) => t(`video_wall.library.assign.day_${d}`)).join(' ')
      : t('video_wall.library.assign.everyday');
    const time = (a.start_time && a.end_time)
      ? `${String(a.start_time).slice(0, 5)}–${String(a.end_time).slice(0, 5)}`
      : t('video_wall.library.assign.always_on');
    return `${days} · ${time}`;
  };

  const wallOptions = walls.map((w) => ({ label: `${w.name} · ${w.rows}×${w.columns}`, value: w.id }));
  const groupOptions = groups.map((g) => ({
    label: `${g.name} · ${g.rows ? `${g.rows}×${g.columns}` : 'any'} · ${g.walls_count ?? 0} walls`,
    value: g.id,
  }));

  return (
    <Dialog
      visible={visible}
      onHide={onHide}
      header={playlist ? t('video_wall.library.assign.title', { name: playlist.name }) : ''}
      style={{ width: 'min(95vw, 560px)' }}
    >
      <div className="d-flex flex-column gap-3 pt-2">
        {/* Existing assignments */}
        <div>
          <label className="d-block mb-1" style={{ color: 'var(--pp-text-secondary)', fontSize: 13 }}>
            {t('video_wall.library.assign.current')}
          </label>
          {existing.length === 0 ? (
            <small style={{ color: 'var(--pp-text-secondary)' }}>{t('video_wall.library.assign.none_yet')}</small>
          ) : existing.map((a) => (
            <div key={a.id} className="d-flex align-items-center justify-content-between p-2 mb-1"
              style={{ background: 'var(--pp-bg-secondary)', borderRadius: 'var(--pp-radius-md)' }}>
              <div style={{ minWidth: 0 }}>
                <div className="text-truncate d-flex align-items-center gap-2" style={{ color: 'var(--pp-text-primary)' }}>
                  {targetLabel(a)}
                  {isScheduled(a) && (
                    <span style={{ background: 'var(--pp-bg-tertiary)', color: 'var(--pp-text-secondary)', fontSize: 10, fontWeight: 600, padding: '1px 7px', borderRadius: 999 }}>
                      <i className="pi pi-calendar me-1" style={{ fontSize: 10 }} />{t('video_wall.library.assign.scheduled_tag')}
                    </span>
                  )}
                </div>
                <small style={{ color: 'var(--pp-text-secondary)' }}>{scheduleSummary(a)}</small>
              </div>
              <Button icon="pi pi-times" rounded text severity="danger"
                aria-label={t('video_wall.library.assign.remove')} onClick={() => del.mutate(a.id)} />
            </div>
          ))}
        </div>

        <hr style={{ borderColor: 'var(--pp-border)', margin: 0 }} />

        {/* New always-on assignment */}
        <div>
          <div className="d-flex gap-2 mb-2">
            <Button label={t('video_wall.library.assign.target_wall')} size="small"
              outlined={targetType !== 'wall'} onClick={() => setTargetType('wall')} />
            <Button label={t('video_wall.library.assign.target_group')} size="small"
              outlined={targetType !== 'group'} onClick={() => setTargetType('group')} />
          </div>
          {targetType === 'wall' ? (
            <Dropdown value={wallId} options={wallOptions} onChange={(e) => setWallId(e.value)}
              placeholder={t('video_wall.library.assign.pick_wall')} className="w-100" filter />
          ) : (
            <Dropdown value={groupId} options={groupOptions} onChange={(e) => setGroupId(e.value)}
              placeholder={t('video_wall.library.assign.pick_group')} className="w-100" filter
              emptyMessage={t('video_wall.library.assign.no_groups')} />
          )}
          <small className="d-block mt-1" style={{ color: 'var(--pp-text-secondary)' }}>
            <i className="pi pi-info-circle me-1" />{t('video_wall.library.assign.geometry_note')}
          </small>
        </div>

        {/* Pointer to the Schedule page for time-based scheduling */}
        <div className="p-2 d-flex align-items-center justify-content-between gap-2 flex-wrap"
          style={{ background: 'var(--pp-bg-secondary)', borderRadius: 'var(--pp-radius-md)' }}>
          <small style={{ color: 'var(--pp-text-secondary)' }}>
            <i className="pi pi-calendar me-1" />{t('video_wall.library.assign.schedule_hint')}
          </small>
          <Button label={t('video_wall.library.assign.open_schedule')} icon="pi pi-arrow-right" iconPos="right"
            size="small" text onClick={() => { onHide?.(); navigate('/apps/video-wall/schedule'); }} />
        </div>

        <div className="d-flex justify-content-end gap-2 pt-2" style={{ borderTop: '1px solid var(--pp-border)' }}>
          <Button label={t('video_wall.library.cancel')} text onClick={onHide} />
          <Button label={t('video_wall.library.assign.assign')} icon="pi pi-check"
            loading={create.isLoading} disabled={!targetId} onClick={submit} />
        </div>
      </div>
    </Dialog>
  );
}
